import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Stack,
  Switch,
  FormControlLabel,
  Paper,
  Chip,
  MenuItem,
  Collapse,
  Autocomplete,
  CircularProgress,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogContentText,
  Snackbar,
  Alert,
  Divider,
  Select,
  TextField,
  IconButton,
  Tooltip,
} from '@mui/material';
import info from '../../assets/info-icon.svg';
import infoDim from '../../assets/info-icon-dim.svg';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckIcon from '@mui/icons-material/Check';
import { ReactComponent as EditIcon } from '../../assets/PepWise2.0/edit-img.svg';
import { ReactComponent as DeleteIcon } from '../../assets/PepWise2.0/delete-img-new.svg';
import AlertErrorDialog from '../DataPipeline/AlertErrorDialog';
import {
  getAlertConfigsByProgram,
  deleteAlertConfigById,
  getIbpAlertProgramHierarchy,
  createAlertConfig,
  updateAlertConfigs,
  addOrUpdateAlertSlaProject,
  fetchJobListSLAConfig,
} from 'service/service';
import RichTextEditor from 'utils/RichTextEmailEditor';
import { buildElasticsearchSourceFields, getEnv } from 'utils/alertPayloadTransformers';

interface ProjectData {
  id?: number | null;
  projects: string;
  alertName?: string;
  sector?: string;
  market?: string;
  alertType: string;
  channel: string;
  recipients: string[];
  more?: number;
  isEditing: boolean;
  Alert_ID?: string;
  HopName?: string;
  templateName?: string;
  payload?: any;
  serviceNowUrl?: string;
  emailBody?: string;
  emailSubject?: string;
  title?: string;
  body?: string;
  teamsMail?: string;
  allRecipients?: string;
}

type AlertType =
  | 'SLA Missed'
  | 'SLA Met'
  | 'SLA Missed, SLA Met'
  | 'Pipeline Failures'
  | 'Data Asset Failures';

interface AlertData {
  hopName: string;
  alertType: string;
  channel: string;
  templateName: string;
  emailBody: string;
  recipients: string;
  deliveryChannel: string;
  projects: string[];
  emailSubject: string;
  payload: {
    description: string;
    ConfigurationItem: string;
    resource: string;
    metric_name: string;
    AssignmentGroup: string;
    severity: string;
    source: string;
    type: string;
  };
  serviceNowUrl: string;
  title: string;
  body: string;
  teamsMail: string;
}

const icon = (
  <Box
    sx={{
      width: 20,
      height: 20,
      border: '2px solid #003771',
      borderRadius: '2px',
      backgroundColor: 'transparent',
    }}
  />
);

const checkedIcon = (
  <Box
    sx={{
      width: 20,
      height: 20,
      border: '2px solid #009BD8',
      borderRadius: '2px',
      backgroundColor: '#009BD8',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      '&::after': {
        content: '""',
        position: 'absolute',
        left: '6px',
        top: '2px',
        width: '5px',
        height: '10px',
        border: 'solid white',
        borderWidth: '0 2px 2px 0',
        transform: 'rotate(45deg)',
      },
    }}
  />
);

const commonSwitchStyles = {
  width: 44,
  height: 22,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: '4px',
    '&.Mui-checked': {
      transform: 'translateX(22px)',
      '& + .MuiSwitch-track': { backgroundColor: '#009BD8', opacity: 1, border: 'none' },
      '& .MuiSwitch-thumb': { backgroundColor: '#EFF6FF' },
    },
    '&.Mui-disabled': {
      '& + .MuiSwitch-track': { backgroundColor: '#71758F', opacity: 0.6, border: '1px solid #71758F' },
      '& .MuiSwitch-thumb': { backgroundColor: '#EFF6FF' },
    },
  },
  '& .MuiSwitch-thumb': { width: 14, height: 14, backgroundColor: '#EFF6FF', boxShadow: 'none' },
  '& .MuiSwitch-track': {
    borderRadius: 14,
    backgroundColor: '#71758F',
    border: '1px solid #71758F',
    boxSizing: 'border-box',
    opacity: 1,
  },
};

const ALL_PROJECTS_VALUE = '__ALL_PROJECTS__';
const ALL_PROJECTS_LABEL = 'All Projects';
const ALERT_TYPE = 'JOB SLA';
const SLA_OPTIONS = ['SLA Met', 'SLA Missed', 'SLA Met, SLA Missed'];
const env = getEnv() || 'prod';
const JOB_SLA_ES_INDEX_NAME = `docs-pepwise_sla_v0.3.0-pepwise_${env}`;

const AlertProjectsPaper = ({ children }: React.HTMLAttributes<HTMLElement>) => (
  <Paper
    sx={{
      backgroundColor: '#FFFFFF !important',
      color: '#003771 !important',
      border: '1px solid #D2E6F9',
      '& .MuiAutocomplete-option': {
        color: '#003771 !important',
        backgroundColor: '#FFFFFF !important',
        '&:hover': { backgroundColor: '#F4F8FC !important' },
        '&[aria-selected="true"]': { backgroundColor: '#E1EBF5 !important' },
      },
    }}
  >
    {children}
  </Paper>
);

export const AlertsTab = ({
  sourceData,
  setSourceData,
  programOptions,
  sectorOptions,
  projectData,
  programName,
  programID,
  showAddForm,
  setShowAddForm,
  newAlertData,
  setNewAlertData,
  editingAlertId,
  setEditingAlertId,
  initialAlertData,
  defaultBodyText,
}: {
  sourceData: any;
  setSourceData: any;
  programOptions: any;
  sectorOptions: any;
  projectData: any;
  programName: string | null;
  programID: string | null;
  showAddForm: boolean;
  setShowAddForm: React.Dispatch<React.SetStateAction<boolean>>;
  newAlertData: AlertData;
  setNewAlertData: React.Dispatch<React.SetStateAction<AlertData>>;
  editingAlertId: string | number | null;
  setEditingAlertId: React.Dispatch<React.SetStateAction<string | number | null>>;
  initialAlertData: AlertData;
  defaultBodyText: string;
}) => {
  const theme = useTheme();
  const [programLevelAlerts, setProgramLevelAlerts] = useState(false);
  const [projectLevelAlerts, setProjectLevelAlerts] = useState(true);
  const [jobLevelAlerts, setJobLevelAlerts] = useState(false);
  const [jobSlaByProject, setJobSlaByProject] = useState<Record<string, any[]>>({});
  const [stageOptions, setStageOptions] = useState<Array<{ projectId: string; projectName: string }>>([]);
  const [isJobListLoading, setIsJobListLoading] = useState(false);
  const [jobTableData, setJobTableData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [errorDialog, setErrorDialog] = useState({ open: false, message: '' });
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, alert: null as any });
  const [tableData, setTableData] = useState<ProjectData[]>([]);
  const [jobShowAddForm, setJobShowAddForm] = useState(false);
  const [jobEditingAlertId, setJobEditingAlertId] = useState<string | null>(null);
  const [jobSaveError, setJobSaveError] = useState<string | null>(null);
  const [jobDeleteDialog, setJobDeleteDialog] = useState<{ open: boolean; alert: any }>({ open: false, alert: null });
  const [jobFormValues, setJobFormValues] = useState({
    projectId: '',
    projectName: '',
    jobNames: [] as string[],
    slaValue: '',
    channel: '',
    emailSubject: '',
    emailBody: '',
    recipients: '',
    serviceNowUrl: '',
    payload: {
      description: '',
      ConfigurationItem: '',
      resource: '',
      metric_name: '',
      AssignmentGroup: '',
      severity: '2',
    },
    title: '',
    teamsMail: '',
    body: '',
  });
  const [programHierarchy, setProgramHierarchy] = useState<any[]>([]);
  const [selectedProgram, setSelectedProgram] = useState<string>('');
  const [selectedSector, setSelectedSector] = useState<string>('');
  const [selectedMarket, setSelectedMarket] = useState<string>('');

  const activeProgramName = selectedProgram || programName || '';
  const currentProgramHierarchy =
    programHierarchy.find((item) => item.programName === activeProgramName) ||
    (programName ? programHierarchy.find((item) => item.programName === programName) || null : null);

  const getUniqueValues = (values: Array<string | undefined | null>) =>
    Array.from(new Set(values.filter((value): value is string => Boolean(value))));

  const sectorOptionsFromHierarchy = getUniqueValues(
    currentProgramHierarchy?.sectors?.map((sector: any) => sector?.sectorName) || [],
  );

  const marketOptionsFromHierarchy = getUniqueValues(
    currentProgramHierarchy?.sectors
      ?.find((sector: any) => sector.sectorName === selectedSector)
      ?.markets?.map((market: any) => market?.marketName) || [],
  );

  const projectOptionsFromHierarchy = getUniqueValues(
    currentProgramHierarchy?.sectors
      ?.find((sector: any) => sector.sectorName === selectedSector)
      ?.markets
      ?.find((market: any) => market.marketName === selectedMarket)
      ?.projects?.map((project: any) => project?.projectName) || [],
  );

  const showError = (message: string) => {
    setErrorDialog({ open: true, message });
  };

  const handleProjectToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setProjectLevelAlerts(event.target.checked);
    if (event.target.checked) {
      setProgramLevelAlerts(false);
    }
  };

  useEffect(() => {
    fetchAlerts();
  }, [programName]);

  useEffect(() => {
    const loadProgramHierarchy = async () => {
      const response = await getIbpAlertProgramHierarchy();
      const hierarchy = response?.data || [];
      const normalizedHierarchy = Array.isArray(hierarchy) ? hierarchy : [];
      setProgramHierarchy(normalizedHierarchy);

      if (programName) {
        const programExists = normalizedHierarchy.some((item: any) => item.programName === programName);
        setSelectedProgram(programExists ? programName : '');
      } else {
        setSelectedProgram('');
      }

      setSelectedSector('');
      setSelectedMarket('');
      setNewAlertData((prev) => ({ ...prev, projects: [] }));
    };

    loadProgramHierarchy();
  }, [programName]);

  useEffect(() => {
    setSelectedSector('');
    setSelectedMarket('');
    setNewAlertData((prev) => ({ ...prev, projects: [] }));
  }, [selectedProgram]);

  useEffect(() => {
    if (editingAlertId) return;
    setSelectedMarket('');
    setNewAlertData((prev) => ({ ...prev, projects: [] }));
  }, [selectedSector, editingAlertId]);

  useEffect(() => {
    if (editingAlertId) return;
    setNewAlertData((prev) => ({ ...prev, projects: [] }));
  }, [selectedMarket, editingAlertId]);

  const handleAddRowClick = () => {
    if (showAddForm && !editingAlertId) {
      handleCancelAdd();
    } else {
      handleCancelAdd();
      setNewAlertData({
        ...initialAlertData,
        emailBody: defaultBodyText,
      });
      setShowAddForm(true);
    }
  };

  const handleCancelAdd = () => {
    setShowAddForm(false);
    setEditingAlertId(null);
    setSaveError(null);
    setSelectedSector('');
    setSelectedMarket('');
    setNewAlertData(initialAlertData);
  };

  const handleEditAlert = (alert: any) => {
    let projectsArray: string[] = [];

    if (alert.projects) {
      projectsArray = Array.isArray(alert.projects)
        ? alert.projects
        : String(alert.projects)
            .split(',')
            .map((item: string) => item.trim())
            .filter(Boolean);
    }

    setNewAlertData({
      hopName: alert.alertName || alert.ruleName || '',
      alertType: alert.alertType || '',
      channel: alert.channel || 'Email',
      templateName: alert.templateName || '',
      emailBody: alert.emailBody || defaultBodyText,
      recipients: alert.allRecipients || '',
      deliveryChannel: '',
      projects: projectsArray,
      emailSubject: alert.emailSubject || '',
      payload: alert.payload || {
        description: '',
        ConfigurationItem: '',
        resource: '',
        metric_name: '',
        AssignmentGroup: '',
        severity: '2',
        source: 'PepWise',
        type: 'incident',
      },
      serviceNowUrl: alert.serviceNowUrl || '',
      title: alert.title || '',
      body: alert.body || '',
      teamsMail: alert.teamsMail || '',
    });

    setSelectedSector(alert.sector && alert.sector !== '-' ? alert.sector : '');
    setSelectedMarket(alert.market && alert.market !== '-' ? alert.market : '');
    setEditingAlertId(alert.Alert_ID || alert.id || null);
    setShowAddForm(true);
  };

  const handleDeleteAlert = (alert: any) => {
    setDeleteDialog({ open: true, alert });
  };

  const cancelDelete = () => {
    setDeleteDialog({ open: false, alert: null });
  };

  const confirmDeleteAlert = async () => {
    const alert = deleteDialog.alert;
    if (!alert) {
      cancelDelete();
      return;
    }

    setIsLoading(true);
    try {
      const result: any = await deleteAlertConfigById(alert.Alert_ID || alert.id);
      if (result?.status === 200 || result?.status === 204 || result?.success) {
        fetchAlerts();
      } else {
        showError('Unable to delete alert.');
      }
    } catch (error) {
      console.error('Failed to delete alert:', error);
      showError('Failed to delete alert.');
    } finally {
      setIsLoading(false);
      cancelDelete();
    }
  };

  const getSelectedProjects = () => {
    const hierarchyProjects = currentProgramHierarchy?.sectors
      ?.find((sector: any) => sector.sectorName === selectedSector)
      ?.markets?.find((market: any) => market.marketName === selectedMarket)?.projects || [];
    const hierarchyProjectNames = hierarchyProjects.map((project: any) => project?.projectName).filter(Boolean);
    const fallbackProjects = projectData || [];
    const fallbackProjectNames = fallbackProjects
      .map((item: any) => item?.projectName || item?.Project_Name)
      .filter(Boolean);
    let selectedProjectNames = newAlertData.projects.filter((projectName) => projectName !== ALL_PROJECTS_VALUE);
    if (newAlertData.projects.includes(ALL_PROJECTS_VALUE)) {
      selectedProjectNames = hierarchyProjectNames.length ? hierarchyProjectNames : fallbackProjectNames;
    }
    const selectedProjects = hierarchyProjects.filter((project: any) =>
      selectedProjectNames.includes(project?.projectName),
    );
    const matchingFallbackProjects = fallbackProjects.filter((project: any) =>
      selectedProjectNames.includes(project?.projectName || project?.Project_Name),
    );
    const finalSelectedProjects = selectedProjects.length ? selectedProjects : matchingFallbackProjects;

    if (!finalSelectedProjects.length) {
      throw new Error('No matching projects found for the selected program hierarchy.');
    }

    const projectIds = finalSelectedProjects.map((project: any) => {
      const projectId = project?.Project_Id || project?.projectId || project?.id || project?._id;
      if (projectId) return projectId;
      const projectName = project?.projectName || project?.Project_Name;
      const match = fallbackProjects.find((item: any) =>
        (item?.projectName || item?.Project_Name) === projectName,
      );
      return match?.Project_Id || match?.projectId || match?.id;
    }).filter(Boolean);

    if (!projectIds.length) {
      throw new Error('No valid project IDs found for the selected projects.');
    }

    return {
      projectIds,
      selectedProjectNames,
      projectNameList: selectedProjectNames.join(', '),
      sectors: Array.from(new Set(finalSelectedProjects
        .map((project: any) => project?.Sectors || project?.sector || selectedSector)
        .filter(Boolean))).join(','),
    };
  };

  const buildAlertConfig = (selectedProgramName: string, projectDetails: ReturnType<typeof getSelectedProjects>) => {
    const selectedProjectNames = projectDetails.selectedProjectNames;
    const ruleName = `${selectedProgramName}-${selectedProjectNames.join('_') || 'ALL'}-${newAlertData.alertType}`
      .replace(/\s+/g, '_')
      .replace(/[^A-Za-z0-9_-]/g, '');
    const projectIdVariable = {
      type: 'cosmos', name: 'Project IDs Variable', identifier: 'projectIds',
      value: JSON.stringify({ collection: 'project_jobs', query: { 'pepwise.body.Project_Id': { $in: projectDetails.projectIds } }, field: 'pepwise.body.Project_Id' }),
      uuid: `var-${Date.now()}-${crypto.randomUUID()}`, isEnabled: true,
    };
    const esQuery = {
      _source: ['pepwise.body.Project_Id', 'pepwise.body.Project_Name', 'pepwise.body.Sla_Status', 'pepwise.body.Schedule_Date'],
      query: { bool: { filter: [
        { term: { 'labels.label.pepwise.metric.kind.name': 'pepwise.metric.project.sla.status' } },
        { terms: { 'pepwise.body.Project_Id': '{{projectIds}}' } },
        { terms: { 'pepwise.body.Sla_Status': ['SLA Missed', 'Not Started', 'In Progress', 'SLA Met'] } },
        { range: { 'pepwise.body.Schedule_Date': { gte: 'now/d', lt: 'now+1d/d' } } },
      ] } }, sort: [{ '@timestamp': { order: 'desc' } }],
    };
    const alertConfig: any = {
      ruleName, alertName: newAlertData.hopName || ruleName, alertStore: true,
      alertType: newAlertData.alertType, pepWiseAlertType: 'ALERT', isNewDataModel: true, isActive: true,
      sourceConfig: { sourceType: 'elasticsearch', indexName: 'logs-pepwise_data_telemetry_metrics_v0.3.0.2025.june-pepwise_qa', query: JSON.stringify(esQuery).replace('"{{projectIds}}"', '{{projectIds}}'), duplicateCheckFields: ['Project_Id', 'Schedule_Date', 'Sla_Status'], keysToExtract: ['Project_Id', 'Project_Name', 'Sla_Status', 'Schedule_Date'] },
      variables: [projectIdVariable], sector: projectDetails.sectors, market: selectedMarket,
      project: projectDetails.projectNameList, program: selectedProgramName,
    };
    if (newAlertData.channel === 'Email') {
      alertConfig.emailConfig = { isEnabled: true, templateName: newAlertData.templateName || 'Default', emailSubject: newAlertData.emailSubject || `PEPWISE ${selectedProgramName} Reporting - SLA Status`, htmlTemplate: newAlertData.emailBody || 'SLA Status of {{Project_Name}}: {{SLA_Status}}', emailRecipients: newAlertData.recipients.split(',').map((recipient) => recipient.trim()).filter(Boolean), channel: 'Email' };
    }
    if (newAlertData.channel === 'SNOW') {
      alertConfig.serviceNowConfig = { isEnabled: true, templateName: newAlertData.templateName || 'Default', payload: { data: { description: newAlertData.payload.description || 'SLA Status of {{Project_Name}}: {{Sla_Status}}', ConfigurationItem: newAlertData.payload.ConfigurationItem || '', resource: newAlertData.payload.resource || '', metric_name: newAlertData.payload.metric_name || '', AssignmentGroup: newAlertData.payload.AssignmentGroup || '', severity: newAlertData.payload.severity || '2', source: 'PepWise', type: 'incident' } }, serviceNowUrl: newAlertData.serviceNowUrl || '', channel: 'Service Now' };
    }
    if (newAlertData.channel === 'MS Teams') {
      alertConfig.teamsConfig = { title: newAlertData.title || `PEPWISE ${selectedProgramName} Reporting - SLA Status`, templateName: newAlertData.templateName || 'Default', bodyTemplate: newAlertData.body || 'SLA Status of {{Project_Name}}: {{Sla_Status}}', teamsMail: newAlertData.teamsMail || '', isEnabled: true, channel: 'Teams' };
    }
    return alertConfig;
  };

  const handleSaveAlert = async () => {
    const selectedProgramName = activeProgramName;
    if (!selectedProgramName) return showError('Program name is required.');
    if (!newAlertData.projects?.length || !selectedSector || !selectedMarket || !newAlertData.alertType || !newAlertData.channel) {
      return showError('Please fill in all required fields.');
    }
    setIsLoading(true);
    setSaveError(null);
    try {
      const projectDetails = getSelectedProjects();
      const alertConfig = buildAlertConfig(selectedProgramName, projectDetails);
      const alertId = editingAlertId?.toString();
      const isEditMode = editingAlertId !== null;
      const response: any = isEditMode && alertId
        ? await updateAlertConfigs(alertId, { ...alertConfig, isCreate: false })
        : await createAlertConfig(alertConfig);
      if (!response?.success || (!isEditMode && !response?.data?.data?._id)) {
        throw new Error(`${isEditMode ? 'Failed to update' : 'Failed to create'} alert configuration in the alert framework.`);
      }
      const savedAlertId = alertId || response.data.data._id;
      let inviteUsers = newAlertData.serviceNowUrl || '';
      if (newAlertData.channel === 'Email') {
        inviteUsers = JSON.stringify(newAlertData.recipients.split(',').map((item) => item.trim()).filter(Boolean));
      } else if (newAlertData.channel === 'MS Teams') {
        inviteUsers = newAlertData.teamsMail || '';
      }
      const deliveryPayload: any = { Alert_ID: savedAlertId, Type: 'Program', DeliveryChannel: newAlertData.channel, AlertType: newAlertData.alertType, TeamChannel: newAlertData.channel, InviteUsers: inviteUsers, TemplateName: newAlertData.templateName || 'Default', Project: projectDetails.projectNameList, ProjectId: programID || sourceData?.id || '' };
      if (newAlertData.channel === 'Email') Object.assign(deliveryPayload, { EmailSubject: newAlertData.emailSubject, EmailBody: newAlertData.emailBody });
      if (newAlertData.channel === 'SNOW') Object.assign(deliveryPayload, { Payload: JSON.stringify(newAlertData.payload), ServiceNowURL: newAlertData.serviceNowUrl });
      if (newAlertData.channel === 'MS Teams') Object.assign(deliveryPayload, { Title: newAlertData.title, Body: newAlertData.body, TeamsMail: newAlertData.teamsMail });
      await addOrUpdateAlertSlaProject(deliveryPayload);
      setShowAddForm(false);
      setEditingAlertId(null);
      setNewAlertData(initialAlertData);
      setSuccessMessage('Alert saved successfully.');
      await fetchAlerts();
    } catch (error: any) {
      console.error('IBP alert save failed:', error);
      showError(error?.message || 'Unable to save alert configuration.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAlerts = async () => {
    if (!programName) {
      setTableData([]);
      return;
    }

    setIsLoading(true);
    try {
      const response: any = await getAlertConfigsByProgram(programName);
      let alerts: any[] = [];
      if (Array.isArray(response?.data?.data?.data)) {
        alerts = response.data.data.data;
      } else if (Array.isArray(response?.data?.data)) {
        alerts = response.data.data;
      }

      const formatted = alerts
        .filter((config: any) => config.program === programName && config.project !== programName)
        .map((config: any) => {
          let channel = 'Unknown';
          let recipientsForTable: string[] = [];
          let allRecipientsString = '';
          let serviceNowUrl = '';
          let teamsMail = '';

          if (config.emailConfig?.isEnabled) {
            channel = 'Email';
            recipientsForTable = config.emailConfig.emailRecipients || [];
            allRecipientsString = recipientsForTable.join(', ');
          } else if (config.serviceNowConfig?.isEnabled) {
            channel = 'Service Now';
            serviceNowUrl = config.serviceNowConfig.serviceNowUrl || '';
            recipientsForTable = serviceNowUrl ? [serviceNowUrl] : [];
            allRecipientsString = serviceNowUrl;
          } else if (config.teamsConfig?.isEnabled) {
            channel = 'MS Teams';
            teamsMail = config.teamsConfig.teamsMail || '';
            recipientsForTable = teamsMail ? [teamsMail] : [];
            allRecipientsString = teamsMail;
          }

          const maxVisible = 3;
          const visibleRecipients = recipientsForTable.slice(0, maxVisible);
          const more = recipientsForTable.length > maxVisible ? recipientsForTable.length - maxVisible : 0;

          return {
            id: config._id,
            projects: config.project || '',
            alertName: config.alertName || config.ruleName || 'Unnamed alert',
            sector: config.sector || '-',
            market: config.market || '-',
            alertType: config.alertType || 'SLA Status',
            channel,
            recipients: visibleRecipients,
            more,
            isEditing: false,
            Alert_ID: config._id,
            ruleName: config.ruleName,
            emailSubject: config.emailConfig?.emailSubject || '',
            emailBody: config.emailConfig?.htmlTemplate || '',
            allRecipients: allRecipientsString,
            templateName:
              config.emailConfig?.templateName ||
              config.serviceNowConfig?.templateName ||
              config.teamsConfig?.templateName ||
              '',
            serviceNowUrl,
            payload: config.serviceNowConfig?.payload?.data || initialAlertData.payload,
            webhookUrl: teamsMail,
            title: config.teamsConfig?.title || '',
            body: config.teamsConfig?.bodyTemplate || '',
            teamsMail,
          };
        });

      setTableData(formatted);
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
      setTableData([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const loadJobList = async () => {
      if (!programID && !sourceData?.id) {
        setJobSlaByProject({});
        return;
      }

      setIsJobListLoading(true);
      try {
        const response: any = await fetchJobListSLAConfig({ programId: programID || sourceData?.id || '' });
        const jobListData = response?.data || {};
        const filterOptions = Array.isArray(jobListData.filterOptions) ? jobListData.filterOptions : [];
        const stageMetadata = new Map<string, string>();
        filterOptions.forEach((project: any) => {
          const projectId = String(project?.projectId || '').trim();
          const projectName = String(project?.projectName || '').trim();
          if (projectId && projectName) stageMetadata.set(projectId, projectName);
        });

        const jobsByProject: Record<string, any[]> = {};
        const stagesWithJobs = new Map<string, { projectId: string; projectName: string }>();
        const slaList = jobListData.slaList && typeof jobListData.slaList === 'object'
          ? jobListData.slaList
          : {};

        Object.entries(slaList).forEach(([projectId, projectJobs]: [string, any]) => {
          const jobs = Array.isArray(projectJobs) ? projectJobs : [];
          const uniqueJobs = new Map<string, any>();

          jobs.forEach((job: any) => {
            const jobKey = String(job?.jobId || job?.jobName || '').trim();
            if (jobKey && !uniqueJobs.has(jobKey)) {
              const projectName = String(
                job?.projectName || stageMetadata.get(projectId) || '',
              ).trim();
              if (projectName) {
                uniqueJobs.set(jobKey, { ...job, projectId, projectName });
              }
            }
          });

          const normalizedJobs = Array.from(uniqueJobs.values());
          if (normalizedJobs.length > 0) {
            jobsByProject[projectId] = normalizedJobs;
            stagesWithJobs.set(projectId, {
              projectId,
              projectName: normalizedJobs[0].projectName,
            });
          }
        });

        setStageOptions(Array.from(stagesWithJobs.values()));
        setJobSlaByProject(jobsByProject);
      } catch (error) {
        console.error('Failed to fetch job level SLA list:', error);
        setJobSlaByProject({});
      } finally {
        setIsJobListLoading(false);
      }
    };

    void loadJobList();
  }, [programID, sourceData?.id]);

  const fetchJobLevelAlerts = async () => {
    if (!programName) {
      setJobTableData([]);
      return;
    }

    setIsLoading(true);
    try {
      const response: any = await getAlertConfigsByProgram(programName);
      let alerts: any[] = [];
      if (Array.isArray(response?.data?.data)) {
        alerts = response.data.data;
      } else if (Array.isArray(response?.data?.data?.data)) {
        alerts = response.data.data.data;
      }

      const jobAlerts = alerts
        .filter((config: any) => config.program === programName && config.sourceConfig?.scope === 'job')
        .map((config: any) => {
          let channel = 'Unknown';
          let allRecipientsString = '';
          let recipientsForTable: string[] = [];
          let serviceNowUrl = '';

          if (config.emailConfig?.isEnabled) {
            channel = 'Email';
            recipientsForTable = config.emailConfig.emailRecipients || [];
            allRecipientsString = recipientsForTable.join(', ');
          } else if (config.serviceNowConfig?.isEnabled) {
            channel = 'Service Now';
            serviceNowUrl = config.serviceNowConfig.serviceNowUrl || '';
            allRecipientsString = serviceNowUrl;
          } else if (config.teamsConfig?.isEnabled) {
            channel = 'MS Teams';
            allRecipientsString = config.teamsConfig.teamsMail || '';
          }

          return {
            id: config._id,
            Alert_ID: config._id,
            jobNames: config.sourceConfig?.jobNames || [],
            jobIds: config.sourceConfig?.jobIds || [],
            slaValue: config.sourceConfig?.slaValue || '',
            projectName: config.project || '',
            alertType: config.alertType || '',
            channel,
            allRecipients: allRecipientsString,
            serviceNowUrl,
            emailSubject: config.emailConfig?.emailSubject || '',
            emailBody: config.emailConfig?.htmlTemplate || '',
            payload: config.serviceNowConfig?.payload?.data || { description: '', ConfigurationItem: '', resource: '', metric_name: '', AssignmentGroup: '', severity: '2' },
            title: config.teamsConfig?.title || '',
            teamsMail: config.teamsConfig?.teamsMail || '',
            body: config.teamsConfig?.bodyTemplate || '',
          };
        });

      setJobTableData(jobAlerts);
      setJobLevelAlerts(!!jobAlerts.length);
    } catch (error) {
      console.error('Failed to fetch job level alerts:', error);
      setJobTableData([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void fetchJobLevelAlerts();
  }, [programName]);

  const jobProjectOptions: Array<{ projectId: string; projectName: string }> = stageOptions;

  const jobOptionsForSelectedProject = jobFormValues.projectId
    ? (jobSlaByProject[jobFormValues.projectId] || []).filter((job: any) =>
        String(job?.projectId) === String(jobFormValues.projectId),
      )
    : [];

  const handleJobAddRowClick = () => {
    if (jobShowAddForm && !jobEditingAlertId) {
      handleJobCancelAdd();
    } else {
      setJobFormValues({
        projectId: '',
        projectName: '',
        jobNames: [],
        slaValue: '',
        channel: '',
        emailSubject: '',
        emailBody: '',
        recipients: '',
        serviceNowUrl: '',
        payload: { description: '', ConfigurationItem: '', resource: '', metric_name: '', AssignmentGroup: '', severity: '2' },
        title: '',
        teamsMail: '',
        body: '',
      });
      setJobEditingAlertId(null);
      setJobSaveError(null);
      setJobShowAddForm(true);
    }
  };

  const handleJobCancelAdd = () => {
    setJobShowAddForm(false);
    setJobEditingAlertId(null);
    setJobSaveError(null);
    setJobFormValues({
      projectId: '',
      projectName: '',
      jobNames: [],
      slaValue: '',
      channel: '',
      emailSubject: '',
      emailBody: '',
      recipients: '',
      serviceNowUrl: '',
      payload: { description: '', ConfigurationItem: '', resource: '', metric_name: '', AssignmentGroup: '', severity: '2' },
      title: '',
      teamsMail: '',
      body: '',
    });
  };

  const handleJobEditAlert = (alert: any) => {
    const projectEntry = jobProjectOptions.find((p: any) => p.projectName === alert.projectName);

    setJobFormValues({
      projectId: projectEntry?.projectId || '',
      projectName: alert.projectName || '',
      jobNames: alert.jobNames || [],
      slaValue: alert.slaValue || '',
      channel: alert.channel === 'Service Now' ? 'Service Now' : alert.channel,
      emailSubject: alert.emailSubject,
      emailBody: alert.emailBody,
      recipients: alert.channel === 'Email' ? alert.allRecipients : '',
      serviceNowUrl: alert.serviceNowUrl,
      payload: alert.payload,
      title: alert.title,
      teamsMail: alert.teamsMail,
      body: alert.body,
    });
    setJobEditingAlertId(alert.Alert_ID);
    setJobShowAddForm(true);
  };

  const handleJobDeleteAlert = (alert: any) => setJobDeleteDialog({ open: true, alert });
  const cancelJobDelete = () => setJobDeleteDialog({ open: false, alert: null });

  const confirmJobDeleteAlert = async () => {
    const alert = jobDeleteDialog.alert;
    if (!alert) return;
    try {
      setIsLoading(true);
      await deleteAlertConfigById(alert.Alert_ID);
      setJobDeleteDialog({ open: false, alert: null });
      await fetchJobLevelAlerts();
    } catch (error: any) {
      showError(`Failed to delete job alert: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const buildJobChannelConfig = (config: any) => {
    if (jobFormValues.channel === 'Email') {
      const emailRecipients = jobFormValues.recipients.split(',').map((s) => s.trim()).filter(Boolean);
      config.emailConfig = {
        isEnabled: true,
        templateName: 'Default',
        emailSubject: jobFormValues.emailSubject || `PEPWISE {{program_name}} - Job Level SLA Alert`,
        htmlTemplate: jobFormValues.emailBody || `<p>The SLA Status of the job {{job_name}} with id {{job_id}} on the schedule date {{schedule_date}} is: {{sla_status}}.</p><p>Program: {{program_name}} <br> Project: {{project_name}}<br>Job Start Time: {{actual_start_time}}<br>Job End Time: {{actual_end_time}}</p>`,
        emailRecipients,
        channel: 'Email',
      };
    } else if (jobFormValues.channel === 'Service Now') {
      config.serviceNowConfig = {
        isEnabled: true,
        templateName: 'Default',
        payload: {
          data: {
            description: jobFormValues.payload?.description || `Job with id {{job_id}} and name {{job_name}} alert type: {{alert_type}}. SLA Status: {{sla_status}}`,
            ConfigurationItem: jobFormValues.payload?.ConfigurationItem || '',
            resource: jobFormValues.payload?.resource || '',
            metric_name: jobFormValues.payload?.metric_name || 'job_alert',
            AssignmentGroup: jobFormValues.payload?.AssignmentGroup || '',
            severity: jobFormValues.payload?.severity || '2',
            source: 'PepWise',
            type: 'incident',
          },
        },
        serviceNowUrl: jobFormValues.serviceNowUrl || '',
        channel: 'Service Now',
      };
    } else if (jobFormValues.channel === 'MS Teams') {
      config.teamsConfig = {
        title: jobFormValues.title || `PEPWISE {{program_name}} - Job Level SLA Alert`,
        templateName: 'Default',
        bodyTemplate: jobFormValues.body || `Job with id {{job_id}} and name {{job_name}} alert type: {{alert_type}}. SLA Status: {{sla_status}}`,
        teamsMail: jobFormValues.teamsMail || '',
        isEnabled: true,
        channel: 'Teams',
      };
    }
  };

  const buildJobSLAAlertConfig = () => {
    const selectedJobs = jobOptionsForSelectedProject.filter((j) => jobFormValues.jobNames.includes(j.jobName));
    const jobIds = selectedJobs.map((j) => j.jobId);
    const jobNames = selectedJobs.map((j) => j.jobName);
    const slaStatusValues = jobFormValues.slaValue === 'SLA Met, SLA Missed' ? ['SLA Met', 'SLA Missed'] : [jobFormValues.slaValue];
    const ruleName = `${programName}-${jobFormValues.projectName}-JOB-SLA-${Date.now()}`.replace(/\s/g, '_');
    const esQuery = {
      _source: buildElasticsearchSourceFields([
        'labels.label.pepwise.metric.job.id',
        'labels.label.pepwise.metric.job.sla.schedule_date',
        'pepwise.body.job_name',
        'pepwise.body.sla_status',
        'pepwise.body.actual_start_time',
        'pepwise.body.actual_end_time',
      ]),
      query: {
        bool: {
          filter: [
            { term: { 'labels.label.pepwise.metric.kind.name': 'pepwise.metric.kind.job.sla.status' } },
            { terms: { 'labels.label.pepwise.metric.job.id': jobIds } },
            { terms: { 'pepwise.body.sla_status': slaStatusValues } },
            { range: { 'pepwise.body.actual_start_time': { gte: 'now-1d' } } },
          ],
        },
      },
      sort: [{ 'labels.label.pepwise.metric.timestamp': { order: 'desc' } }],
    };

    const config: any = {
      ruleName,
      alertStore: true,
      alertType: ALERT_TYPE,
      pepWiseAlertType: 'ALERT',
      isNewDataModel: true,
      isActive: !!(programID || sourceData?.id),
      sourceConfig: {
        sourceType: 'elasticsearch',
        scope: 'job',
        indexName: JOB_SLA_ES_INDEX_NAME,
        query: JSON.stringify(esQuery),
        duplicateCheckFields: ['job_id', 'schedule_date'],
        keysToExtract: [{ key: 'labels.label.pepwise.metric.job.id', alias: 'job_id' }, 'job_name', 'sla_status', { key: 'labels.label.pepwise.metric.job.sla.schedule_date', alias: 'schedule_date' }, 'actual_start_time', 'actual_end_time'],
        jobIds,
        jobNames,
        slaValue: jobFormValues.slaValue,
      },
      sector: 'ALL',
      project: jobFormValues.projectName,
      program: programName,
    };

    buildJobChannelConfig(config);
    return config;
  };

  const getJobSaveValidationError = () => {
    const requiredFields = [
      [!jobFormValues.projectId, 'Please select a stage.'],
      [jobFormValues.jobNames.length === 0, 'Please select at least one job.'],
      [!jobFormValues.slaValue, 'Please select an SLA option.'],
      [!jobFormValues.channel, 'Please select a delivery channel.'],
    ] as const;
    const requiredFieldError = requiredFields.find(([invalid]) => invalid)?.[1];
    if (requiredFieldError) return requiredFieldError;

    if (jobFormValues.channel === 'Email' && !jobFormValues.recipients.split(',').some((recipient) => recipient.trim())) {
      return 'Please enter at least one recipient email.';
    }
    if (jobFormValues.channel === 'MS Teams' && !jobFormValues.teamsMail.trim()) {
      return 'Please enter a Teams Mail address.';
    }
    return null;
  };

  const persistJobAlertConfig = async (alertConfig: any) => {
    if (jobEditingAlertId) {
      const response: any = await updateAlertConfigs(jobEditingAlertId, { ...alertConfig, isCreate: false });
      if (!response.success) throw new Error('Failed to update job level alert configuration.');
      return;
    }

    const response: any = await createAlertConfig(alertConfig);
    if (!response.success || !response.data?.data?._id) {
      throw new Error('Failed to create job level alert configuration.');
    }
  };

  const handleJobSaveAlert = async () => {
    try {
      setIsLoading(true);
      setJobSaveError(null);

      const validationError = getJobSaveValidationError();
      if (validationError) {
        showError(validationError);
        return;
      }

      await persistJobAlertConfig(buildJobSLAAlertConfig());
      handleJobCancelAdd();
      await fetchJobLevelAlerts();
    } catch (error: any) {
      const errorMsg = error?.message || 'Failed to save job level alert. Please try again.';
      setJobSaveError(errorMsg);
      showError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const renderJobRecipientChips = (channel: string, recipients: string[], moreCount?: number, allRecipientsStr?: string) => {
    if (channel === 'Email') {
      const maxVisible = 3;
      const visibleRecipients = recipients.slice(0, maxVisible);
      const actualMore = recipients.length > maxVisible ? recipients.length - maxVisible : (moreCount || 0);
      return (
        <Box display="flex" flexWrap="wrap" alignItems="center" gap={0.5}>
          {visibleRecipients.map((rec) => {
            const displayEmail = rec.length > 20 ? `${rec.substring(0, 17)}...` : rec;
            return (
              <Chip
                key={rec}
                label={displayEmail}
                size="small"
                sx={{ backgroundColor: '#E1EBF5', color: '#003771', fontWeight: 500, fontSize: '0.75rem', borderRadius: '16px', height: '24px' }}
              />
            );
          })}
          {actualMore > 0 ? (
            <Typography sx={{ fontSize: '0.75rem', color: '#00AEEF', fontWeight: 'bold', ml: 0.5 }}>
              +{actualMore} more...
            </Typography>
          ) : null}
        </Box>
      );
    }

    if (channel === 'Service Now') {
      return (
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography
            variant="body2"
            sx={{ color: '#009BD8', textDecoration: 'underline', cursor: 'pointer', fontWeight: 500, fontSize: '0.85rem', wordBreak: 'break-all' }}
            onClick={() => allRecipientsStr && window.open(allRecipientsStr, '_blank')}
          >
            {allRecipientsStr}
          </Typography>
        </Box>
      );
    }

    if (channel === 'MS Teams') {
      return (
        <Typography variant="body2" sx={{ color: '#003771', fontWeight: 500, fontSize: '0.85rem', wordBreak: 'break-all' }}>
          {allRecipientsStr}
        </Typography>
      );
    }

    return null;
  };

  const renderJobChips = (jobNames: string[]) => {
    if (!jobNames || jobNames.length === 0) return null;
    return (
      <Box display="flex" flexWrap="wrap" gap={0.5}>
        {jobNames.map((name, idx) => (
          <Tooltip key={`${name}-${idx}`} title={name} placement="bottom">
            <Chip
              label={
                <Box display="flex" alignItems="center" gap={0.5}>
                  <span>{name}</span>
                  <span style={{ fontSize: '10px', opacity: 0.6, cursor: 'default' }}>×</span>
                </Box>
              }
              size="small"
              sx={{ backgroundColor: '#E1EBF5', color: '#003771', fontWeight: 'bold', fontSize: '0.75rem', borderRadius: '16px', height: '24px', maxWidth: 200 }}
            />
          </Tooltip>
        ))}
      </Box>
    );
  };

  const renderJobAlertForm = () => {
    let saveButtonContent: React.ReactNode = 'Save';
    if (isLoading) {
      saveButtonContent = <CircularProgress size={20} sx={{ color: 'white' }} />;
    } else if (jobEditingAlertId) {
      saveButtonContent = 'Update';
    }
    const blueThemeInputStyles = {
      width: '100%',
      backgroundColor: '#F4F8FC',
      borderRadius: '4px',
      '& .MuiInputLabel-root': {
        color: '#5A6A85 !important',
        '&.Mui-focused': { color: '#003771 !important' },
        '&.MuiInputLabel-shrink': { color: '#003771 !important' },
      },
      '& .MuiInputBase-input': { color: '#003771 !important', '-webkit-text-fill-color': '#003771 !important' },
      '& .MuiOutlinedInput-root': {
        backgroundColor: '#F4F8FC',
        '& fieldset': { borderColor: '#D2E6F9 !important' },
        '&:hover fieldset': { borderColor: '#009BD8 !important' },
        '&.Mui-focused fieldset': { borderColor: '#009BD8 !important' },
        '& input': { color: '#003771 !important' },
      },
      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D2E6F9 !important' },
      '& .MuiSvgIcon-root': { color: '#009BD8 !important' },
      '& .MuiSelect-icon': { color: '#009BD8 !important' },
      '& .MuiSelect-select': { color: '#003771 !important' },
      '& .MuiChip-root': {
        backgroundColor: '#D0E8F9 !important',
        color: '#003771 !important',
        fontWeight: 500,
        borderRadius: '16px !important',
        margin: '2px !important',
        '& .MuiChip-label': { color: '#003771 !important' },
        '& .MuiChip-deleteIcon': { color: '#003771 !important', '&:hover': { color: '#001E3E !important' } },
      },
    };

    const lightPaperComponent = ({ children }: any) => (
      <Paper
        sx={{
          backgroundColor: '#FFFFFF !important',
          color: '#003771 !important',
          border: '1px solid #D2E6F9',
          boxShadow: '0px 4px 16px rgba(0, 55, 113, 0.08)',
          '& .MuiAutocomplete-option': {
            color: '#003771 !important',
            backgroundColor: '#FFFFFF !important',
            '&:hover': { backgroundColor: '#F4F8FC !important' },
            '&[aria-selected="true"]': { backgroundColor: '#E1EBF5 !important' },
          },
          '& .MuiAutocomplete-noOptions': {
            display: 'block !important',
            color: '#003771 !important',
            backgroundColor: '#FFFFFF !important',
            padding: '12px 16px !important',
          },
        }}
      >
        {children}
      </Paper>
    );

    const icon = (
      <Box
        sx={{
          width: 20,
          height: 20,
          border: '2px solid #003771',
          borderRadius: '2px',
          backgroundColor: 'transparent',
        }}
      />
    );

    const checkedIcon = (
      <Box
        sx={{
          width: 20,
          height: 20,
          border: '2px solid #009BD8',
          borderRadius: '2px',
          backgroundColor: '#009BD8',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            left: '6px',
            top: '2px',
            width: '5px',
            height: '10px',
            border: 'solid white',
            borderWidth: '0 2px 2px 0',
            transform: 'rotate(45deg)',
          },
        }}
      />
    );

    return (
      <Paper
        sx={{
          mt: 2,
          mb: 3,
          p: '24px !important',
          backgroundColor: 'transparent !important',
          color: '#003771 !important',
          border: '1px solid #D2E6F9 !important',
          boxShadow: 'none',
          borderRadius: '8px',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ color: '#009BD8', fontWeight: 600, whiteSpace: 'nowrap', fontSize: '18px' }}>
            {jobEditingAlertId ? 'Edit Job Level Alert' : 'Add Jobs & Alert Types'}
          </Typography>
          <Divider sx={{ flexGrow: 1, backgroundColor: '#009BD8 !important', height: '1px' }} />
        </Box>

        {jobSaveError && (
          <Typography color="error" sx={{ mt: 1, mb: 1, whiteSpace: 'pre-wrap' }}>
            Error: {jobSaveError}
          </Typography>
        )}

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 3, mt: 2.5, mb: 3.5 }}>
          <Autocomplete
            sx={{ gridColumn: '1 / span 1' }}
            options={jobProjectOptions}
            getOptionLabel={(option: any) => option.projectName || ''}
            value={jobProjectOptions.find((p: any) => p.projectId === jobFormValues.projectId) || null}
            noOptionsText={isJobListLoading ? 'Loading...' : 'No Data'}
            loadingText="Loading..."
            loading={isJobListLoading}
            popupIcon={<ExpandMoreIcon sx={{ color: '#009BD8 !important' }} />}
            onChange={(_event: any, newValue: any) => {
              setJobFormValues((prev) => ({
                ...prev,
                projectId: newValue?.projectId || '',
                projectName: newValue?.projectName || '',
                jobNames: [],
              }));
            }}
            renderOption={(props, option: any) => {
              const { key, ...optionProps } = props;
              return (
                <li key={key} {...optionProps} style={{ backgroundColor: '#FFFFFF', color: '#003771' }}>
                  {option.projectName}
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField {...params} variant="outlined" placeholder="Select Stage*" sx={blueThemeInputStyles} />
            )}
            PaperComponent={lightPaperComponent}
          />

          <Autocomplete
            sx={{ gridColumn: '2 / span 1' }}
            options={SLA_OPTIONS}
            value={jobFormValues.slaValue || null}
            popupIcon={<ExpandMoreIcon sx={{ color: '#009BD8 !important' }} />}
            onChange={(_event, newValue) => setJobFormValues((prev) => ({ ...prev, slaValue: newValue || '' }))}
            getOptionLabel={(option) => (option ? `${ALERT_TYPE} - ${option}` : '')}
            renderOption={(props, option) => {
              const { key, ...optionProps } = props;
              return (
                <li key={key} {...optionProps} style={{ backgroundColor: '#FFFFFF', color: '#003771' }}>
                  {`${ALERT_TYPE} - ${option}`}
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField {...params} variant="outlined" placeholder="Select Alert*" sx={blueThemeInputStyles} />
            )}
            PaperComponent={lightPaperComponent}
          />

          <Autocomplete
            multiple
            disableCloseOnSelect
            sx={{ gridColumn: '1 / span 2' }}
            options={jobOptionsForSelectedProject.map((j) => j.jobName).filter(Boolean)}
            value={jobFormValues.jobNames}
            disabled={!jobFormValues.projectId}
            noOptionsText={isJobListLoading ? 'Loading...' : 'No Data'}
            loadingText="Loading..."
            popupIcon={<ExpandMoreIcon sx={{ color: '#009BD8 !important' }} />}
            onChange={(_event, newValue) => setJobFormValues((prev) => ({ ...prev, jobNames: newValue }))}
            renderOption={(props, option, { selected }) => {
              const { key, ...optionProps } = props;
              return (
                <li key={key} {...optionProps} style={{ backgroundColor: '#FFFFFF', color: '#003771' }}>
                  <Checkbox
                    icon={icon}
                    checkedIcon={checkedIcon}
                    style={{ marginRight: 8, color: '#003771' }}
                    checked={selected}
                    sx={{ color: '#003771 !important', fill: '#003771 !important', '&.Mui-checked': { color: '#009BD8 !important', fill: '#009BD8 !important' } }}
                  />
                  {option}
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField {...params} variant="outlined" placeholder="Select Job(s)*" sx={blueThemeInputStyles} />
            )}
            PaperComponent={lightPaperComponent}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 3.5, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ color: '#009BD8', fontWeight: 600, whiteSpace: 'nowrap', fontSize: '18px' }}>
            Configure Delivery Channels
          </Typography>
          <Divider sx={{ flexGrow: 1, backgroundColor: '#009BD8 !important', height: '1px' }} />
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 2.5 }}>
          <Box sx={{ borderRadius: '4px', overflow: 'hidden' }}>
            <Box onClick={() => setJobFormValues((prev) => ({ ...prev, channel: 'Email' }))} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.2, px: 1.5, cursor: 'pointer' }}>
              <Box display="flex" alignItems="center" gap={1.5} sx={{ flexGrow: 1 }}>
                <Switch
                  checked={jobFormValues.channel === 'Email'}
                  onChange={(e) => {
                    if (e.target.checked) setJobFormValues((prev) => ({ ...prev, channel: 'Email' }));
                  }}
                  size="small"
                  sx={commonSwitchStyles}
                />
                <Typography sx={{ color: '#009BD8 !important', fontWeight: '600 !important', fontSize: '0.95rem', whiteSpace: 'nowrap' }}>
                  Email Channel
                </Typography>
                {jobFormValues.channel === 'Email' && (
                  <Divider sx={{ flexGrow: 1, backgroundColor: '#009BD8 !important', height: '1px', ml: 1, mr: 1 }} />
                )}
              </Box>
              <IconButton size="small" sx={{ color: '#009BD8' }}>
                {jobFormValues.channel === 'Email' ? <ExpandLessIcon sx={{ color: '#009BD8' }} /> : <ExpandMoreIcon sx={{ color: '#009BD8' }} />}
              </IconButton>
            </Box>
            <Collapse in={jobFormValues.channel === 'Email'} timeout="auto" unmountOnExit>
              <Box sx={{ pt: 2, pb: 1, px: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField fullWidth placeholder="Email Subject*" variant="outlined" sx={blueThemeInputStyles} value={jobFormValues.emailSubject} onChange={(e) => setJobFormValues((prev) => ({ ...prev, emailSubject: e.target.value }))} />
                <TextField fullWidth placeholder="Job {{job_name}} alert: {{alert_type}}" variant="outlined" multiline rows={4} sx={blueThemeInputStyles} value={jobFormValues.emailBody} onChange={(e) => setJobFormValues((prev) => ({ ...prev, emailBody: e.target.value }))} />
                <TextField fullWidth placeholder="Select Recipient Emails or DL" variant="outlined" sx={blueThemeInputStyles} value={jobFormValues.recipients} onChange={(e) => setJobFormValues((prev) => ({ ...prev, recipients: e.target.value }))} required />
              </Box>
            </Collapse>
          </Box>

          <Box sx={{ borderRadius: '4px', overflow: 'hidden' }}>
            <Box onClick={() => setJobFormValues((prev) => ({ ...prev, channel: 'Service Now' }))} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.2, px: 1.5, cursor: 'pointer' }}>
              <Box display="flex" alignItems="center" gap={1.5} sx={{ flexGrow: 1 }}>
                <Switch
                  checked={jobFormValues.channel === 'Service Now'}
                  onChange={(e) => {
                    if (e.target.checked) setJobFormValues((prev) => ({ ...prev, channel: 'Service Now' }));
                  }}
                  size="small"
                  sx={commonSwitchStyles}
                />
                <Typography sx={{ color: '#009BD8', fontWeight: 600, fontSize: '0.95rem', whiteSpace: 'nowrap' }}>
                  Service Now Channel
                </Typography>
                {jobFormValues.channel === 'Service Now' && (
                  <Divider sx={{ flexGrow: 1, backgroundColor: '#009BD8 !important', ml: 1, mr: 1 }} />
                )}
              </Box>
              <IconButton size="small" sx={{ color: '#009BD8' }}>
                {jobFormValues.channel === 'Service Now' ? <ExpandLessIcon sx={{ color: '#009BD8' }} /> : <ExpandMoreIcon sx={{ color: '#009BD8' }} />}
              </IconButton>
            </Box>
            <Collapse in={jobFormValues.channel === 'Service Now'} timeout="auto" unmountOnExit>
              <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 3 }}>
                <TextField fullWidth label="Description" variant="outlined" multiline rows={4} sx={blueThemeInputStyles} value={jobFormValues.payload?.description || ''} onChange={(e) => setJobFormValues((prev) => ({ ...prev, payload: { ...prev.payload, description: e.target.value } }))} />
                <Box sx={{ display: 'flex', gap: 3 }}>
                  <TextField fullWidth label="Configuration Item" variant="outlined" sx={blueThemeInputStyles} value={jobFormValues.payload?.ConfigurationItem || ''} onChange={(e) => setJobFormValues((prev) => ({ ...prev, payload: { ...prev.payload, ConfigurationItem: e.target.value } }))} />
                  <TextField fullWidth label="Resource" variant="outlined" sx={blueThemeInputStyles} value={jobFormValues.payload?.resource || ''} onChange={(e) => setJobFormValues((prev) => ({ ...prev, payload: { ...prev.payload, resource: e.target.value } }))} />
                </Box>
                <Box sx={{ display: 'flex', gap: 3 }}>
                  <TextField fullWidth label="Metric Name" variant="outlined" sx={blueThemeInputStyles} value={jobFormValues.payload?.metric_name || ''} onChange={(e) => setJobFormValues((prev) => ({ ...prev, payload: { ...prev.payload, metric_name: e.target.value } }))} />
                  <TextField fullWidth label="Assignment Group" variant="outlined" sx={blueThemeInputStyles} value={jobFormValues.payload?.AssignmentGroup || ''} onChange={(e) => setJobFormValues((prev) => ({ ...prev, payload: { ...prev.payload, AssignmentGroup: e.target.value } }))} />
                </Box>
                <Box sx={{ display: 'flex', gap: 3 }}>
                  <TextField fullWidth label="Severity" variant="outlined" sx={blueThemeInputStyles} value={jobFormValues.payload?.severity || ''} onChange={(e) => setJobFormValues((prev) => ({ ...prev, payload: { ...prev.payload, severity: e.target.value } }))} placeholder="2" />
                  <TextField fullWidth label="Service Now URL" variant="outlined" sx={blueThemeInputStyles} value={jobFormValues.serviceNowUrl || ''} onChange={(e) => setJobFormValues((prev) => ({ ...prev, serviceNowUrl: e.target.value }))} />
                </Box>
              </Box>
            </Collapse>
          </Box>

          <Box sx={{ borderRadius: '4px', overflow: 'hidden' }}>
            <Box onClick={() => setJobFormValues((prev) => ({ ...prev, channel: 'MS Teams' }))} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.2, px: 1.5, cursor: 'pointer' }}>
              <Box display="flex" alignItems="center" gap={1.5} sx={{ flexGrow: 1 }}>
                <Switch
                  checked={jobFormValues.channel === 'MS Teams'}
                  onChange={(e) => {
                    if (e.target.checked) setJobFormValues((prev) => ({ ...prev, channel: 'MS Teams' }));
                  }}
                  size="small"
                  sx={commonSwitchStyles}
                />
                <Typography sx={{ color: '#009BD8', fontWeight: 600, fontSize: '0.95rem', whiteSpace: 'nowrap' }}>
                  MS Teams Channel
                </Typography>
                {jobFormValues.channel === 'MS Teams' && (
                  <Divider sx={{ flexGrow: 1, backgroundColor: '#009BD8 !important', ml: 1, mr: 1 }} />
                )}
              </Box>
              <IconButton size="small" sx={{ color: '#009BD8' }}>
                {jobFormValues.channel === 'MS Teams' ? <ExpandLessIcon sx={{ color: '#009BD8' }} /> : <ExpandMoreIcon sx={{ color: '#009BD8' }} />}
              </IconButton>
            </Box>
            <Collapse in={jobFormValues.channel === 'MS Teams'} timeout="auto" unmountOnExit>
              <Box sx={{ pt: 2, pb: 1, px: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField fullWidth placeholder="Title*" variant="outlined" sx={blueThemeInputStyles} value={jobFormValues.title} onChange={(e) => setJobFormValues((prev) => ({ ...prev, title: e.target.value }))} />
                <TextField fullWidth placeholder="Job {{job_name}} alert: {{alert_type}}" variant="outlined" multiline rows={4} sx={blueThemeInputStyles} value={jobFormValues.body} onChange={(e) => setJobFormValues((prev) => ({ ...prev, body: e.target.value }))} />
                <TextField fullWidth placeholder="Teams Mail*" variant="outlined" sx={blueThemeInputStyles} value={jobFormValues.teamsMail || ''} onChange={(e) => setJobFormValues((prev) => ({ ...prev, teamsMail: e.target.value }))} required />
              </Box>
            </Collapse>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 3, gap: 2 }}>
          <Button
            variant="pepwiseButtonText"
            onClick={handleJobCancelAdd}
            sx={{ color: '#00A3E0', fontWeight: 700, fontSize: '0.85rem', letterSpacing: '0.5px', textTransform: 'uppercase', '&:hover': { backgroundColor: 'transparent', color: '#0088BC' } }}
          >
            CANCEL
          </Button>
          <Button
            variant="pepwiseSecondary"
            onClick={handleJobSaveAlert}
            sx={{ backgroundColor: '#00A3E0', color: 'white !important', fontWeight: 600, textTransform: 'uppercase', width: '108px !important', minWidth: '108px !important', maxWidth: '108px !important', height: '36px', p: 0, borderRadius: '5px', boxShadow: 'none', '&:hover': { backgroundColor: '#0088BC', boxShadow: 'none' } }}
            disabled={isLoading}
          >
            {saveButtonContent}
          </Button>
        </Box>
      </Paper>
    );
  };

  const renderTableChips = (
    value: string | string[] | undefined,
    onClick?: () => void,
    moreCount?: number,
  ) => {
    const values = (Array.isArray(value) ? value : String(value || '').split(','))
      .map((item) => item.trim())
      .filter(Boolean)
      .map((item) => (item === ALL_PROJECTS_VALUE ? ALL_PROJECTS_LABEL : item));

    return (
      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
        {values.map((item, index) => (
          <Tooltip key={`${item}-${index}`} title={item} placement="top" arrow>
            <Chip
              label={item}
              size="small"
              onClick={onClick}
              sx={{
                backgroundColor: '#bdd9f3 !important',
                color: '#35658A !important',
                maxWidth: '100%',
                height: 'auto',
                minHeight: '24px',
                marginRight: '5px',
                borderRadius: '20px',
                '& .MuiChip-label': {
                  color: '#35658A !important',
                  padding: '4px 10px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
                ...(onClick && {
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: '#bdd9f3 !important' },
                }),
              }}
            />
          </Tooltip>
        ))}
        {(moreCount ?? 0) > 0 && (
          <Typography sx={{ ...theme.typography.tableRowFont, color: '#009BD8' }}>
            +{moreCount} more...
          </Typography>
        )}
      </Stack>
    );
  };

  const renderAddAlertForm = () => {
    const programOptionsList = Array.from(new Set(programHierarchy.map((item) => item.programName).filter(Boolean)));
    const projectOptions = [
      ALL_PROJECTS_VALUE,
      ...Array.from(new Set([
        ...projectOptionsFromHierarchy,
        ...(projectData || []).map((p: any) => p?.projectName || p?.Project_Name).filter(Boolean),
      ])),
    ];
    const saveButtonLabel = editingAlertId ? 'Update' : 'Save';

    const alertTypeOptions = [
      'SLA Missed',
      'Not Started',
      'In Progress',
      'SLA Met',
    ];

    const blueThemeInputStyles = {
      width: '100%',
      backgroundColor: '#F4F8FC',
      borderRadius: '4px',
      '& .MuiInputLabel-root': {
        color: '#5A6A85 !important',
        '&.Mui-focused': { color: '#003771 !important' },
        '&.MuiInputLabel-shrink': { color: '#003771 !important' },
      },
      '& .MuiInputBase-input': {
        color: '#003771 !important',
        '-webkit-text-fill-color': '#003771 !important',
      },
      '& .MuiOutlinedInput-root': {
        backgroundColor: '#F4F8FC',
        '& fieldset': { borderColor: '#D2E6F9 !important' },
        '&:hover fieldset': { borderColor: '#009BD8 !important' },
        '&.Mui-focused fieldset': { borderColor: '#009BD8 !important' },
        '& input': { color: '#003771 !important' },
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#D2E6F9 !important',
      },
      '& .MuiSvgIcon-root': { color: '#009BD8 !important' },
      '& .MuiSelect-icon': { color: '#009BD8 !important' },
      '& .MuiSelect-select': { color: '#003771 !important' },
      '& .MuiChip-root': {
        backgroundColor: '#D0E8F9 !important',
        color: '#003771 !important',
        fontWeight: 500,
        borderRadius: '16px !important',
        margin: '2px !important',
        '& .MuiChip-label': { color: '#003771 !important' },
        '& .MuiChip-deleteIcon': {
          color: '#003771 !important',
          '&:hover': { color: '#001E3E !important' },
        },
      },
    };

    const lightPaperComponent = ({ children }: any) => (
      <Paper
        sx={{
          backgroundColor: '#FFFFFF !important',
          color: '#003771 !important',
          border: '1px solid #D2E6F9',
          boxShadow: '0px 4px 16px rgba(0, 55, 113, 0.08)',
          '& .MuiAutocomplete-option': {
            color: '#003771 !important',
            backgroundColor: '#FFFFFF !important',
            '&:hover': { backgroundColor: '#F4F8FC !important' },
            '&[aria-selected="true"]': { backgroundColor: '#E1EBF5 !important' },
          },
        }}
      >
        {children}
      </Paper>
    );

    const renderChannelSection = (label: string, channelValue: string, children: React.ReactNode) => (
      <Box sx={{ borderRadius: '4px', overflow: 'hidden', border: '1px solid #D2E6F9' }}>
        <Box
          onClick={() => setNewAlertData((prev) => ({ ...prev, channel: channelValue }))}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 1.5,
            py: 1.2,
            cursor: 'pointer',
            backgroundColor: newAlertData.channel === channelValue ? '#EAF5FF' : '#F7FBFF',
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5} sx={{ flexGrow: 1 }}>
            <Switch
              checked={newAlertData.channel === channelValue}
              onChange={(e) => {
                if (e.target.checked) {
                  setNewAlertData((prev) => ({ ...prev, channel: channelValue }));
                }
              }}
              size="small"
              sx={commonSwitchStyles}
            />
            <Typography sx={{ color: '#009BD8', fontWeight: 600, fontSize: '0.95rem', whiteSpace: 'nowrap' }}>
              {label}
            </Typography>
          </Box>
          <IconButton size="small" sx={{ color: '#009BD8' }}>
            {newAlertData.channel === channelValue ? (
              <ExpandLessIcon sx={{ color: '#009BD8' }} />
            ) : (
              <ExpandMoreIcon sx={{ color: '#009BD8' }} />
            )}
          </IconButton>
        </Box>
        <Collapse in={newAlertData.channel === channelValue} timeout="auto" unmountOnExit>
          <Box sx={{ p: 2, backgroundColor: '#F7FBFF', borderTop: '1px solid #D2E6F9' }}>
            {children}
          </Box>
        </Collapse>
      </Box>
    );

    return (
      <Paper
        sx={{
          mt: 2,
          mb: 3,
          p: '24px !important',
          backgroundColor: 'transparent !important',
          color: '#003771 !important',
          border: '1px solid #D2E6F9 !important',
          boxShadow: 'none',
          borderRadius: '8px',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ color: '#009BD8', fontWeight: 600, whiteSpace: 'nowrap', fontSize: '18px' }}>
            {editingAlertId ? 'Edit Projects & Alert Types' : 'Add Projects & Alert Types'}
          </Typography>
          <Divider sx={{ flexGrow: 1, backgroundColor: '#009BD8 !important', height: '1px' }} />
        </Box>

        {saveError && (
          <Typography color="error" sx={{ mt: 1, mb: 1, whiteSpace: 'pre-wrap' }}>
            Error: {saveError}
          </Typography>
        )}

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 3, mt: 2.5, mb: 3.5 }}>
          <Autocomplete
            fullWidth
            options={programOptionsList}
            value={activeProgramName || null}
            disabled={!programOptionsList.length}
            onChange={(_event, newValue) => setSelectedProgram(newValue || '')}
            popupIcon={<ExpandMoreIcon sx={{ color: '#009BD8 !important' }} />}
            renderInput={(params) => (
              <TextField {...params} variant="outlined" placeholder="Select Program*" sx={blueThemeInputStyles} />
            )}
            renderOption={(props, option) => {
              const { key, ...optionProps } = props;
              return (
                <li key={key} {...optionProps} style={{ backgroundColor: '#FFFFFF', color: '#003771' }}>
                  {option}
                </li>
              );
            }}
            PaperComponent={lightPaperComponent}
          />

          <Autocomplete
            fullWidth
            options={sectorOptionsFromHierarchy}
            value={selectedSector || null}
            disabled={!selectedProgram}
            onChange={(_event, newValue) => {
              setSelectedSector(newValue || '');
              setSelectedMarket('');
              setNewAlertData((prev) => ({ ...prev, projects: [] }));
            }}
            popupIcon={<ExpandMoreIcon sx={{ color: '#009BD8 !important' }} />}
            renderInput={(params) => (
              <TextField {...params} variant="outlined" placeholder="Select Sector*" sx={blueThemeInputStyles} />
            )}
            renderOption={(props, option) => {
              const { key, ...optionProps } = props;
              return (
                <li key={key} {...optionProps} style={{ backgroundColor: '#FFFFFF', color: '#003771' }}>
                  {option}
                </li>
              );
            }}
            PaperComponent={lightPaperComponent}
          />

          <Autocomplete
            fullWidth
            options={marketOptionsFromHierarchy}
            value={selectedMarket || null}
            disabled={!selectedSector}
            onChange={(_event, newValue) => {
              setSelectedMarket(newValue || '');
              setNewAlertData((prev) => ({ ...prev, projects: [] }));
            }}
            popupIcon={<ExpandMoreIcon sx={{ color: '#009BD8 !important' }} />}
            renderInput={(params) => (
              <TextField {...params} variant="outlined" placeholder="Select Market*" sx={blueThemeInputStyles} />
            )}
            renderOption={(props, option) => {
              const { key, ...optionProps } = props;
              return (
                <li key={key} {...optionProps} style={{ backgroundColor: '#FFFFFF', color: '#003771' }}>
                  {option}
                </li>
              );
            }}
            PaperComponent={lightPaperComponent}
          />

          <TextField
            fullWidth
            value={newAlertData.hopName}
            onChange={(e) => setNewAlertData((prev) => ({ ...prev, hopName: e.target.value }))}
            placeholder="Alert Name"
            variant="outlined"
            sx={blueThemeInputStyles}
          />

          <Autocomplete
            multiple
            fullWidth
            disableCloseOnSelect
            disabled={!selectedMarket}
            options={projectOptions}
            value={newAlertData.projects}
            popupIcon={<ExpandMoreIcon sx={{ color: '#009BD8 !important' }} />}
            onChange={(event, newValue) => {
              let updatedValue = [...newValue];
              const previousProjects = newAlertData.projects;
              const lastSelected = updatedValue.at(-1);
              const includesAll = updatedValue.includes(ALL_PROJECTS_VALUE);
              const previousIncludedAll = previousProjects.includes(ALL_PROJECTS_VALUE);
              const actualProjectCount = projectOptions.length - 1;

              if (lastSelected === ALL_PROJECTS_VALUE && !previousIncludedAll) {
                updatedValue = projectOptions;
              } else if (newValue.length < previousProjects.length && !includesAll && previousIncludedAll) {
                updatedValue = [];
              } else if (newValue.length === actualProjectCount && !includesAll && actualProjectCount > 0) {
                updatedValue = projectOptions;
              } else if (includesAll && lastSelected !== ALL_PROJECTS_VALUE && newValue.length < projectOptions.length) {
                updatedValue = updatedValue.filter((value) => value !== ALL_PROJECTS_VALUE);
              } else {
                updatedValue = newValue;
              }

              setNewAlertData((prev: AlertData) => ({ ...prev, projects: updatedValue }));
            }}
            getOptionLabel={(option) => (option === ALL_PROJECTS_VALUE ? ALL_PROJECTS_LABEL : option)}
            renderOption={(props, option, { selected }) => (
              <li {...props} style={{ backgroundColor: '#FFFFFF', color: '#003771' }}>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8, color: '#003771' }}
                  checked={selected}
                  sx={{
                    color: '#003771 !important',
                    fill: '#003771 !important',
                    '&.Mui-checked': { color: '#009BD8 !important', fill: '#009BD8 !important' },
                  }}
                />
                {option === ALL_PROJECTS_VALUE ? ALL_PROJECTS_LABEL : option}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder={newAlertData.projects.length ? '' : 'Select Projects*'}
                sx={blueThemeInputStyles}
              />
            )}
            PaperComponent={AlertProjectsPaper}
          />

          <Select
            fullWidth
            value={newAlertData.alertType}
            onChange={(e) => setNewAlertData((prev: AlertData) => ({ ...prev, alertType: e.target.value }))}
            displayEmpty
            IconComponent={ExpandMoreIcon}
            renderValue={(selected) => {
              if (!selected) {
                return <span style={{ color: '#5A6A85' }}>Select Alert*</span>;
              }
              return selected;
            }}
            sx={blueThemeInputStyles}
            inputProps={{ 'aria-label': 'Select Alert' }}
            MenuProps={{
              PaperProps: {
                sx: {
                  backgroundColor: '#FFFFFF !important',
                  color: '#003771 !important',
                  border: '1px solid #D2E6F9',
                  boxShadow: '0px 4px 16px rgba(0, 55, 113, 0.08)',
                  '& .MuiList-root, & .MuiMenu-list': {
                    backgroundColor: '#FFFFFF !important',
                    paddingTop: '0px !important',
                    paddingBottom: '0px !important',
                  },
                  '& .MuiMenuItem-root': {
                    color: '#003771 !important',
                    backgroundColor: '#FFFFFF !important',
                    '&:hover': { backgroundColor: '#F4F8FC !important' },
                  },
                },
              },
              MenuListProps: {
                sx: {
                  backgroundColor: '#FFFFFF !important',
                  paddingTop: '0px !important',
                  paddingBottom: '0px !important',
                },
              },
            }}
          >
            <MenuItem value="" disabled hidden sx={{ display: 'none' }}>
              <span style={{ color: '#5A6A85' }}>Select Alert*</span>
            </MenuItem>
            {alertTypeOptions.map((option) => {
              const isSelected = newAlertData.alertType === option;
              return (
                <MenuItem
                  key={option}
                  value={option}
                  sx={{
                    color: '#003771 !important',
                    backgroundColor: isSelected ? '#D8F0FA !important' : '#FFFFFF !important',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 1.2,
                    px: 2,
                    fontSize: '13px',
                    '&:hover': {
                      backgroundColor: isSelected ? '#D8F0FA !important' : '#F4F8FC !important',
                    },
                  }}
                >
                  <Typography sx={{ fontSize: '13px', color: '#003771', fontWeight: isSelected ? 500 : 400 }}>
                    {option}
                  </Typography>
                  {isSelected && <CheckIcon sx={{ fontSize: '18px', color: '#003771' }} />}
                </MenuItem>
              );
            })}
          </Select>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 3.5, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ color: '#009BD8', fontWeight: 600, whiteSpace: 'nowrap', fontSize: '18px' }}>
            Configure Delivery Channels
          </Typography>
          <Divider sx={{ flexGrow: 1, backgroundColor: '#009BD8 !important', height: '1px' }} />
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 2.5 }}>
          {renderChannelSection('Email Channel', 'Email', (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                placeholder="Email Subject*"
                variant="outlined"
                sx={blueThemeInputStyles}
                value={newAlertData.emailSubject || 'PEPWISE Reporting - SLA Status'}
                onChange={(e) => setNewAlertData((prev: AlertData) => ({ ...prev, emailSubject: e.target.value }))}
              />
              <TextField
                fullWidth
                placeholder="Select Recipient Emails or DL"
                variant="outlined"
                sx={blueThemeInputStyles}
                value={newAlertData.recipients}
                onChange={(e) => setNewAlertData((prev: AlertData) => ({ ...prev, recipients: e.target.value }))}
              />
              <Box>
                <Typography variant="caption" sx={{ color: '#5A6A85 !important', mb: 0.5, display: 'block' }}>
                  Email Body
                </Typography>
                <RichTextEditor
                  value={newAlertData.emailBody || 'SLA Status of {{Project_Name}}: {{SLA_Status}}'}
                  onChange={(value) => setNewAlertData((prev: AlertData) => ({ ...prev, emailBody: value }))}
                  textColor="#003771"
                  showToolbar={false}
                />
              </Box>
            </Box>
          ))}

          {renderChannelSection('Service Now Channel', 'Service Now', (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                label="Description"
                variant="outlined"
                multiline
                rows={4}
                sx={blueThemeInputStyles}
                value={newAlertData.payload.description}
                onChange={(e) =>
                  setNewAlertData((prev: AlertData) => ({
                    ...prev,
                    payload: { ...prev.payload, description: e.target.value },
                  }))
                }
              />
              <Box sx={{ display: 'flex', gap: 3 }}>
                <TextField
                  fullWidth
                  label="Configuration Item"
                  variant="outlined"
                  sx={blueThemeInputStyles}
                  value={newAlertData.payload.ConfigurationItem}
                  onChange={(e) =>
                    setNewAlertData((prev: AlertData) => ({
                      ...prev,
                      payload: { ...prev.payload, ConfigurationItem: e.target.value },
                    }))
                  }
                />
                <TextField
                  fullWidth
                  label="Resource"
                  variant="outlined"
                  sx={blueThemeInputStyles}
                  value={newAlertData.payload.resource}
                  onChange={(e) =>
                    setNewAlertData((prev: AlertData) => ({
                      ...prev,
                      payload: { ...prev.payload, resource: e.target.value },
                    }))
                  }
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 3 }}>
                <TextField
                  fullWidth
                  label="Metric Name"
                  variant="outlined"
                  sx={blueThemeInputStyles}
                  value={newAlertData.payload.metric_name}
                  onChange={(e) =>
                    setNewAlertData((prev: AlertData) => ({
                      ...prev,
                      payload: { ...prev.payload, metric_name: e.target.value },
                    }))
                  }
                />
                <TextField
                  fullWidth
                  label="Assignment Group"
                  variant="outlined"
                  sx={blueThemeInputStyles}
                  value={newAlertData.payload.AssignmentGroup}
                  onChange={(e) =>
                    setNewAlertData((prev: AlertData) => ({
                      ...prev,
                      payload: { ...prev.payload, AssignmentGroup: e.target.value },
                    }))
                  }
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 3 }}>
                <TextField
                  fullWidth
                  label="Severity"
                  variant="outlined"
                  sx={blueThemeInputStyles}
                  value={newAlertData.payload.severity}
                  onChange={(e) =>
                    setNewAlertData((prev: AlertData) => ({
                      ...prev,
                      payload: { ...prev.payload, severity: e.target.value },
                    }))
                  }
                  placeholder="2"
                />
                <TextField
                  fullWidth
                  label="Service Now URL"
                  variant="outlined"
                  sx={blueThemeInputStyles}
                  value={newAlertData.serviceNowUrl}
                  onChange={(e) => setNewAlertData((prev: AlertData) => ({ ...prev, serviceNowUrl: e.target.value }))}
                />
              </Box>
            </Box>
          ))}

          {renderChannelSection('MS Teams Channel', 'MS Teams', (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                label="Title"
                variant="outlined"
                sx={blueThemeInputStyles}
                value={newAlertData.title || 'PEPWISE Reporting - SLA Status'}
                onChange={(e) => setNewAlertData((prev: AlertData) => ({ ...prev, title: e.target.value }))}
              />
              <TextField
                fullWidth
                label="Teams Mail"
                variant="outlined"
                sx={blueThemeInputStyles}
                value={newAlertData.teamsMail}
                onChange={(e) => setNewAlertData((prev: AlertData) => ({ ...prev, teamsMail: e.target.value }))}
              />
              <RichTextEditor
                value={newAlertData.body || 'SLA Status of {{Project_Name}}: {{SLA_Status}}'}
                onChange={(value) => setNewAlertData((prev: AlertData) => ({ ...prev, body: value }))}
                textColor="#003771"
              />
            </Box>
          ))}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 3, gap: 2 }}>
          <Button
            variant="pepwiseButtonText"
            onClick={handleCancelAdd}
            sx={{
              color: '#00A3E0',
              fontWeight: 700,
              fontSize: '0.85rem',
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              '&:hover': { backgroundColor: 'transparent', color: '#0088BC' },
            }}
          >
            CANCEL
          </Button>
          <Button
            variant="pepwiseSecondary"
            onClick={handleSaveAlert}
            sx={{
              backgroundColor: '#00A3E0',
              color: 'white !important',
              fontWeight: 600,
              textTransform: 'uppercase',
              width: '108px !important',
              minWidth: '108px !important',
              maxWidth: '108px !important',
              height: '36px',
              p: 0,
              borderRadius: '5px',
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#0088BC', boxShadow: 'none' },
            }}
            disabled={isLoading}
          >
            {isLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : saveButtonLabel}
          </Button>
        </Box>
      </Paper>
    );
  };

  let addAlertLabel = 'Add Project Level Alert';
  if (editingAlertId) {
    addAlertLabel = 'Cancel Edit';
  } else if (showAddForm) {
    addAlertLabel = 'Cancel';
  }

  let jobAddAlertLabel = 'Add Job Level Alert';
  if (jobEditingAlertId) {
    jobAddAlertLabel = 'Cancel Edit';
  } else if (jobShowAddForm) {
    jobAddAlertLabel = 'Cancel';
  }

  return (
    <Box
      sx={{
        mt: 3,
        mx: 4,
        py: 3,
        px: 0,
        backgroundColor: '#EFF6FF',
        color: '#003771',
        borderRadius: '8px',
      }}
    >
      <Typography
        variant="body2"
        sx={{
          opacity: 0.7,
          mb: 3,
          color: 'textLight.main',
          display: 'flex',
          gap: '4px',
          minWidth: 'max-content',
          fontStyle: 'italic',
        }}
      >
        <img src={info} alt="" />
        <span>This section is to configure alerts.</span>
      </Typography>

      <Typography
        variant="subHeader"
        sx={{
          color: '#009BD8',
          mb: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          fontWeight: 600,
        }}
      >
        Enable Alerts
        <Tooltip title="" placement="top">
          <Box component="img" src={infoDim} alt="info" sx={{ width: 20, height: 20, cursor: 'pointer' }} />
        </Tooltip>
        <Divider sx={{ flexGrow: 1, backgroundColor: '#009BD8', marginLeft: 0.5 }} />
      </Typography>

      <Stack spacing={2} sx={{ mb: 3, gap: '16px' }}>
        <FormControlLabel
          control={
            <Switch
              checked={projectLevelAlerts}
              onChange={handleProjectToggle}
              size="small"
              sx={commonSwitchStyles}
            />
          }
          label={
            <Typography sx={{ color: '#000000 !important', fontWeight: 500, marginLeft: '10px', fontSize: '14px' }}>
              Configure Project Level Alerts
            </Typography>
          }
        />
      </Stack>

      {projectLevelAlerts && (
        <>
          <Box mt={2} sx={{ borderRadius: '8px', border: '1px solid #D2E6F9', overflow: 'hidden' }}>
            <Box
              display="flex"
              sx={{
                backgroundColor: '#003771',
                borderBottom: '1px solid rgba(255,255,255,0.2)',
                color: 'white !important',
              }}
            >
              <Typography sx={{ width: '12%', fontWeight: 'bold', color: 'white', py: 1.5, px: 2, borderRight: '1px solid rgba(255,255,255,0.2)', fontSize: '0.85rem' }}>
                PROJECTS
              </Typography>
              <Typography sx={{ width: '16%', fontWeight: 'bold', color: 'white', py: 1.5, px: 2, borderRight: '1px solid rgba(255,255,255,0.2)', fontSize: '0.85rem' }}>
                ALERT NAME
              </Typography>
              <Typography sx={{ width: '12%', fontWeight: 'bold', color: 'white', py: 1.5, px: 2, borderRight: '1px solid rgba(255,255,255,0.2)', fontSize: '0.85rem' }}>
                SECTOR
              </Typography>
              <Typography sx={{ width: '12%', fontWeight: 'bold', color: 'white', py: 1.5, px: 2, borderRight: '1px solid rgba(255,255,255,0.2)', fontSize: '0.85rem' }}>
                MARKET
              </Typography>
              <Typography sx={{ width: '18%', fontWeight: 'bold', color: 'white', py: 1.5, px: 2, borderRight: '1px solid rgba(255,255,255,0.2)', fontSize: '0.85rem' }}>
                ALERT TYPE
              </Typography>
              <Typography sx={{ width: '12%', fontWeight: 'bold', color: 'white', py: 1.5, px: 2, borderRight: '1px solid rgba(255,255,255,0.2)', fontSize: '0.85rem' }}>
                CHANNELS
              </Typography>
              <Typography sx={{ width: '14%', fontWeight: 'bold', color: 'white', py: 1.5, px: 2, borderRight: '1px solid rgba(255,255,255,0.2)', fontSize: '0.85rem' }}>
                RECIPIENT/URL
              </Typography>
              <Typography sx={{ width: '8%', textAlign: 'center', fontWeight: 'bold', color: 'white', py: 1.5, px: 2, fontSize: '0.85rem' }}>
                ACTIONS
              </Typography>
            </Box>

            {isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress sx={{ color: '#003771' }} />
              </Box>
            )}
            {!isLoading && tableData.length === 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4, backgroundColor: '#F4F8FC' }}>
                <Typography sx={{ color: '#003771' }}>No alerts configured for this program.</Typography>
              </Box>
            )}
            {!isLoading && tableData.length > 0 && (
              tableData.map((row) => (
                <Box
                  key={row.id}
                  display="flex"
                  alignItems="center"
                  sx={{
                    py: 1.5,
                    minHeight: '52px',
                    backgroundColor: '#F4F8FC',
                    borderBottom: '1px solid #D2E6F9',
                    '&:last-child': { borderBottom: 'none' },
                  }}
                >
                  <Typography
                    variant="tableRowFont"
                    sx={{
                      width: '12%',
                      color: '#003771',
                      ...theme.typography.tableRowFont,
                      px: 2,
                      wordBreak: 'break-all',
                    }}
                  >
                    {renderTableChips(row.projects)}
                  </Typography>

                  <Typography
                    variant="tableRowFont"
                    sx={{ width: '16%', color: '#003771', ...theme.typography.tableRowFont, px: 2, wordBreak: 'break-all' }}
                  >
                    {row.alertName}
                  </Typography>

                  <Typography
                    variant="tableRowFont"
                    sx={{ width: '12%', color: '#003771', ...theme.typography.tableRowFont, px: 2, wordBreak: 'break-all' }}
                  >
                    {renderTableChips(row.sector)}
                  </Typography>

                  <Typography
                    variant="tableRowFont"
                    sx={{ width: '12%', color: '#003771', ...theme.typography.tableRowFont, px: 2, wordBreak: 'break-all' }}
                  >
                    {renderTableChips(row.market)}
                  </Typography>

                  <Typography
                    variant="tableRowFont"
                    sx={{ width: '18%', color: '#003771', ...theme.typography.tableRowFont, px: 2, wordBreak: 'break-all' }}
                  >
                    {row.alertType}
                  </Typography>

                  <Typography
                    variant="tableRowFont"
                    sx={{ width: '12%', color: '#003771', ...theme.typography.tableRowFont, px: 2, wordBreak: 'break-all' }}
                  >
                    {row.channel}
                  </Typography>

                  <Box sx={{ width: '14%', color: '#003771', ...theme.typography.tableRowFont, px: 2 }}>
                    {row.channel === 'Service Now'
                      ? renderTableChips(row.serviceNowUrl, () => window.open(row.serviceNowUrl, '_blank'))
                      : renderTableChips(row.recipients, undefined, row.more)}
                  </Box>

                  <Box sx={{ width: '8%', textAlign: 'center' }}>
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      <IconButton size="small" sx={{ color: '#009BD8' }} onClick={() => handleEditAlert(row)} disabled={isLoading}>
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" sx={{ color: '#E53E3E' }} onClick={() => handleDeleteAlert(row)} disabled={isLoading}>
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </Box>
                </Box>
              ))
            )}
          </Box>

          <Typography
            variant="labelText"
            onClick={handleAddRowClick}
            sx={{
              color: '#009BD8',
              cursor: 'pointer',
              mt: 2,
              display: 'flex',
              alignItems: 'center',
              ...theme.typography.labelText,
            }}
          >
            {showAddForm && !editingAlertId ? <RemoveIcon sx={{ mr: 1 }} /> : <AddIcon sx={{ mr: 1 }} />}
            {addAlertLabel}
          </Typography>

          <Collapse in={showAddForm} timeout="auto" unmountOnExit>
            {renderAddAlertForm()}
          </Collapse>
        </>
      )}

      <FormControlLabel
        sx={{ ml: '0px', mt: 3 }}
        control={
          <Switch
            checked={jobLevelAlerts}
            onChange={(e) => setJobLevelAlerts(e.target.checked)}
            size="small"
            sx={commonSwitchStyles}
          />
        }
        label={
          <Typography sx={{ color: '#000000 !important', fontWeight: 500, marginLeft: '10px', fontSize: '14px' }}>
            Configure Job Level Alerts
          </Typography>
        }
      />

      {jobLevelAlerts && (
        <>
          <Box mt={2} sx={{ borderRadius: '8px', border: '1px solid #D2E6F9', overflow: 'hidden' }}>
            <Box display="flex" sx={{ backgroundColor: '#003771', borderBottom: '1px solid rgba(255, 255, 255, 0.2)', color: 'white !important' }}>
              <Typography sx={{ width: '20%', fontWeight: 'bold', color: 'white', py: 1.5, px: 2, borderRight: '1px solid rgba(255,255,255,0.2)', fontSize: '0.85rem' }}>JOBS</Typography>
              <Typography sx={{ width: '15%', fontWeight: 'bold', color: 'white', py: 1.5, px: 2, borderRight: '1px solid rgba(255,255,255,0.2)', fontSize: '0.85rem' }}>PROJECT</Typography>
              <Typography sx={{ width: '20%', fontWeight: 'bold', color: 'white', py: 1.5, px: 2, borderRight: '1px solid rgba(255,255,255,0.2)', fontSize: '0.85rem' }}>ALERT TYPE</Typography>
              <Typography sx={{ width: '15%', fontWeight: 'bold', color: 'white', py: 1.5, px: 2, borderRight: '1px solid rgba(255,255,255,0.2)', fontSize: '0.85rem' }}>CHANNEL</Typography>
              <Typography sx={{ width: '20%', fontWeight: 'bold', color: 'white', py: 1.5, px: 2, borderRight: '1px solid rgba(255,255,255,0.2)', fontSize: '0.85rem' }}>RECIPIENT/URL</Typography>
              <Typography sx={{ width: '10%', textAlign: 'center', fontWeight: 'bold', color: 'white', py: 1.5, px: 2, fontSize: '0.85rem' }}>ACTIONS</Typography>
            </Box>

            {isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4, backgroundColor: '#F4F8FC' }}>
                <CircularProgress sx={{ color: '#003771' }} />
              </Box>
            )}
            {!isLoading && jobTableData.length === 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4, backgroundColor: '#F4F8FC' }}>
                <Typography sx={{ color: '#003771', fontWeight: 500 }}>No job level alerts configured for this program.</Typography>
              </Box>
            )}
            {!isLoading && jobTableData.length > 0 && (
              jobTableData.map((row) => {
                let recipientContent: React.ReactNode;
                if (row.channel === 'Email') {
                  const recipients = row.allRecipients
                    ? String(row.allRecipients).split(',').map((s: string) => s.trim()).filter(Boolean)
                    : [];
                  recipientContent = renderJobRecipientChips('Email', recipients);
                } else if (row.channel === 'Service Now') {
                  recipientContent = renderJobRecipientChips('Service Now', [], 0, String(row.allRecipients || ''));
                } else {
                  recipientContent = renderJobRecipientChips('MS Teams', [], 0, String(row.allRecipients || ''));
                }

                return (
                <Box key={row.id} display="flex" alignItems="center" sx={{ backgroundColor: '#F4F8FC', borderBottom: '1px solid #D2E6F9', '&:last-child': { borderBottom: 'none' } }}>
                  <Box display="flex" alignItems="center" sx={{ width: '20%', borderRight: '1px solid #D2E6F9', py: 1.5, px: 2 }}>
                    {renderJobChips(row.jobNames)}
                  </Box>
                  <Box display="flex" alignItems="center" sx={{ width: '15%', borderRight: '1px solid #D2E6F9', py: 1.5, px: 2 }}>
                    <Typography sx={{ color: '#003771', fontWeight: 500, fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.projectName}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" sx={{ width: '20%', borderRight: '1px solid #D2E6F9', py: 1.5, px: 2 }}>
                    <Typography sx={{ color: '#003771', fontWeight: 500, fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.slaValue ? `${row.alertType} - ${row.slaValue}` : row.alertType}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" sx={{ width: '15%', borderRight: '1px solid #D2E6F9', py: 1.5, px: 2 }}>
                    <Typography sx={{ color: '#003771', fontWeight: 500, fontSize: '0.85rem' }}>{row.channel}</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" sx={{ width: '20%', borderRight: '1px solid #D2E6F9', py: 1.5, px: 2 }}>
                    {recipientContent}
                  </Box>
                  <Box display="flex" alignItems="center" justifyContent="center" sx={{ width: '10%', py: 1.5, px: 2 }}>
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      <IconButton size="small" sx={{ color: '#009BD8' }} onClick={() => handleJobEditAlert(row)} disabled={isLoading}>
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" sx={{ color: '#E53E3E' }} onClick={() => handleJobDeleteAlert(row)} disabled={isLoading}>
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </Box>
                </Box>
                );
              })
            )}
          </Box>

          <Typography variant="body2" onClick={handleJobAddRowClick} sx={{ cursor: 'pointer', mt: 2, display: 'flex', alignItems: 'center', color: '#009BD8 !important', fontWeight: 600 }}>
            {jobShowAddForm && !jobEditingAlertId ? <RemoveIcon sx={{ mr: 1 }} /> : <AddIcon sx={{ mr: 1 }} />}
            {jobAddAlertLabel}
          </Typography>

          <Collapse in={jobShowAddForm} timeout="auto" unmountOnExit>
            {renderJobAlertForm()}
          </Collapse>

          <Dialog
            open={jobDeleteDialog.open}
            onClose={cancelJobDelete}
            PaperProps={{ sx: { backgroundColor: 'rgb(0, 55, 113)', color: 'white', border: '1px solid rgba(255, 255, 255, 0.12)' } }}
          >
            <DialogTitle sx={{ color: 'white' }}>Confirm Delete</DialogTitle>
            <DialogContent>
              <DialogContentText color="inherit" sx={{ color: 'white' }}>
                Are you sure you want to delete this job level alert for stage "{jobDeleteDialog.alert?.projectName || jobDeleteDialog.alert?.project || ''}"?
                <br />
                This action cannot be undone.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={cancelJobDelete} sx={{ color: 'rgba(255, 255, 255, 0.7)', textTransform: 'none' }}>
                Cancel
              </Button>
              <Button onClick={confirmJobDeleteAlert} sx={{ backgroundColor: '#E53E3E', color: 'white', textTransform: 'none', '&:hover': { backgroundColor: '#D32F2F' } }} disabled={isLoading}>
                {isLoading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Delete'}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      <AlertErrorDialog
        open={errorDialog.open}
        errorMessage={errorDialog.message}
        onClose={() => setErrorDialog({ open: false, message: '' })}
      />

      <Dialog
        open={deleteDialog.open}
        onClose={cancelDelete}
        PaperProps={{
          sx: {
            backgroundColor: 'rgb(0, 55, 113)',
            color: 'white',
            border: '1px solid rgba(255, 255, 255, 0.12)',
          },
        }}
      >
        <DialogTitle sx={{ color: 'white' }}>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText color="inherit" sx={{ color: 'white' }}>
            Are you sure you want to delete this alert for "{deleteDialog.alert?.projects}"?
            <br />
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete} sx={{ color: 'rgba(255, 255, 255, 0.7)', textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteAlert}
            sx={{
              backgroundColor: '#E53E3E',
              color: 'white',
              textTransform: 'none',
              '&:hover': { backgroundColor: '#D32F2F' },
            }}
            disabled={isLoading}
          >
            {isLoading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessMessage(null)} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AlertsTab;
